import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";
import { adminSliceSelectors } from "../../../redux/admin/admin.selector";
import { adminActions } from "../../../redux/admin/admin.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { AdminMessage } from "./AdminMessage";
import { Info } from "../../../components/Info";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";

const MarkButton = styled(Button)`
  width: fit-content;
  margin: 0;
  padding: 0.6rem 1.5rem;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

export const NotificationsAdminMessagesTab: React.FC = () => {
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);

  const { t } = useTranslation(undefined, {
    keyPrefix: "header.bell.drawer.adminMessages",
  });
  const dispatch = useAppDispatch();

  const adminMessages = useAppSelector(adminSliceSelectors.selectAdminMessages);
  const adminMessagesLoading = useAppSelector(
    adminSliceSelectors.selectAdminMessagesLoading,
  );
  const adminMessagesPage = useAppSelector(
    adminSliceSelectors.selectAdminMessagesPage,
  );
  const adminMessagesPageSize = useAppSelector(
    adminSliceSelectors.selectAdminMessagesPageSize,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const buttonDisabled = useMemo(() => {
    return (
      adminMessages?.every((m) => m?.read === true) || !adminMessages?.length
    );
  }, [adminMessages]);

  const onMarkAsRead = async () => {
    try {
      setMarkAsReadLoading(true);

      await dispatch(adminActions.markAdminMessagesAsRead({ userId })).unwrap();
      await dispatch(
        adminActions.getAdminMessages({
          userId,
          meta: { page: adminMessagesPage - 1, count: adminMessagesPageSize },
        }),
      ).unwrap();

      AtiraToast.success(t("markAsRead.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setMarkAsReadLoading(false);
    }
  };

  if (adminMessagesLoading && !adminMessages?.length) {
    return <SpinnerFullScreen />;
  }

  return (
    <Flex flexDirection="column" gap="l" minHeight={"100%"}>
      {adminMessages?.length ? (
        <Flex
          justifyContent="space-between"
          gap="s"
          alignItems="center"
          flexWrap="wrap"
        >
          <Flex flex={1} alignItems="center">
            <Info info={t("title")} width="100%" />
          </Flex>
          <MarkButton
            loading={markAsReadLoading}
            disabled={buttonDisabled}
            onClick={onMarkAsRead}
            icon={faEye}
          >
            {t("mark_as_read")}
          </MarkButton>
        </Flex>
      ) : null}

      {adminMessages?.length ? (
        <Flex flexDirection="column" gap="m">
          {adminMessages.map((message) => (
            <AdminMessage key={message._id} message={message} />
          ))}
        </Flex>
      ) : (
        <AtiraEmpty description={t("empty")} />
      )}
    </Flex>
  );
};
